import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faTrash, 
  faEye, 
  faEyeSlash,
  faMapMarkerAlt,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons';

const LocationsList = ({ 
  locations, 
  onEdit, 
  onDelete, 
  onToggleStatus,
  loading = false 
}) => {
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const handleDelete = async (location) => {
    if (window.confirm(`Are you sure you want to delete "${location.city}, ${location.state}"? This action cannot be undone.`)) {
      setDeletingId(location.id);
      try {
        await onDelete(location.id);
      } catch (error) {
        console.error('Error deleting location:', error);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleToggleStatus = async (location) => {
    setTogglingId(location.id);
    try {
      await onToggleStatus(location.id, !location.isActive);
    } catch (error) {
      console.error('Error toggling location status:', error);
    } finally {
      setTogglingId(null);
    }
  };

  const formatZipCodes = (zipCodes) => {
    if (!zipCodes || zipCodes.length === 0) return 'All ZIP codes';
    if (zipCodes.length <= 3) return zipCodes.join(', ');
    return `${zipCodes.slice(0, 3).join(', ')} +${zipCodes.length - 3} more`;
  };

  const formatDeliveryFee = (fee) => {
    const amount = Number(fee) || 0;
    return amount === 0 ? 'Free' : `$${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="loading-indicator">
        Loading locations...
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="empty-state">
        <FontAwesomeIcon icon={faMapMarkerAlt} size="3x" />
        <h3>No Locations Found</h3>
        <p>Start by adding your first service location.</p>
      </div>
    );
  }

  return (
    <div className="locations-list">
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>City</th>
              <th>State</th>
              <th>ZIP Codes</th>
              <th>Delivery Fee</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {locations.map(location => (
              <tr key={location.id} className={!location.isActive ? 'inactive-row' : ''}>
                <td>
                  <div className="location-cell">
                    <FontAwesomeIcon 
                      icon={faMapMarkerAlt} 
                      className={`location-icon ${location.isActive ? 'active' : 'inactive'}`}
                    />
                    <span className="city-name">{location.city}</span>
                  </div>
                </td>
                <td>
                  <span className="state-badge">{location.state}</span>
                </td>
                <td>
                  <span className="zip-codes" title={location.zipCodes?.join(', ') || 'All ZIP codes'}>
                    {formatZipCodes(location.zipCodes)}
                  </span>
                </td>
                <td>
                  <span className={`delivery-fee ${location.deliveryFee === 0 ? 'free' : 'paid'}`}>
                    {formatDeliveryFee(location.deliveryFee)}
                  </span>
                </td>
                <td>
                  <button
                    onClick={() => handleToggleStatus(location)}
                    className={`status-toggle ${location.isActive ? 'active' : 'inactive'}`}
                    disabled={togglingId === location.id}
                    title={`Click to ${location.isActive ? 'deactivate' : 'activate'}`}
                  >
                    {togglingId === location.id ? (
                      <span className="loading-text">...</span>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={location.isActive ? faCheck : faTimes} />
                        {location.isActive ? 'Active' : 'Inactive'}
                      </>
                    )}
                  </button>
                </td>
                <td>
                  <span className="created-date">
                    {location.createdAt ? location.createdAt.toLocaleDateString() : 'Unknown'}
                  </span>
                </td>
                <td>
                  <div className="actions-cell">
                    <button
                      onClick={() => onEdit(location)}
                      className="icon-button edit"
                      title="Edit location"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    
                    <button
                      onClick={() => handleToggleStatus(location)}
                      className={`icon-button ${location.isActive ? 'view' : 'save'}`}
                      disabled={togglingId === location.id}
                      title={location.isActive ? 'Deactivate location' : 'Activate location'}
                    >
                      <FontAwesomeIcon icon={location.isActive ? faEyeSlash : faEye} />
                    </button>
                    
                    <button
                      onClick={() => handleDelete(location)}
                      className="icon-button delete"
                      disabled={deletingId === location.id}
                      title="Delete location"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="locations-summary">
        <p>
          Total: {locations.length} location{locations.length !== 1 ? 's' : ''} | 
          Active: {locations.filter(loc => loc.isActive).length} | 
          Inactive: {locations.filter(loc => !loc.isActive).length}
        </p>
      </div>
    </div>
  );
};

export default LocationsList; 